import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getUserActionedArticleOrNews, rateArticle, saveArticleOrNews } from "services";

import {
  cleanHtml,
  formatDate,
  recordPageView,
  useCancelAsyncOnUnmount,
  useGetFullArticleOrNews,
  useNavigate,
  useParams,
  useTranslation,
  useUserContext,
} from "utils";

import { ARTICLE_NEWS } from "consts";

import { ARTICLE_TYPE, Article, RATING_STATUS, UserAction } from "models";

import { Col, Divider, ErrorBoundary, Icon, Image, LazyLoadImage, Row, ScreenError, Share, SignUpNudgeCard, SubPage } from "components";

import { RelatedArticles } from "pages/app/articles-news/components/RelatedArticles";
import { RelatedNews } from "pages/app/articles-news/components/RelatedNews";
import { Taglist } from "pages/app/articles-news/components/Taglist";
import { UserActions } from "pages/app/articles-news/components/UserActions";

const ArticlesAndNews = ({ type }: { type: ARTICLE_TYPE }) => {
  const { t } = useTranslation(["news", "meta", "global", "articles", "notes"]);
  const { user } = useUserContext();
  const { link } = useParams();
  const navigate = useNavigate();

  const { article: articleOrNews, articleIsLoading: loading } = useGetFullArticleOrNews({ link: link || "", type });

  const [currentLink, setCurrentLink] = useState<string | null>(null);
  const [amountOfParagraphs, setAmountOfParagraphs] = useState<number>(0);
  const [html, setHtml] = useState<JSX.Element[]>([]);
  const [image, setImage] = useState(false);
  const [userActions, setUserActions] = useState<UserAction>({
    allowed: false,
    saved: false,
    success: true,
    rating: RATING_STATUS.NONE,
  });

  const [share, setShare] = useState<Share>({
    title: t(`meta:${type}.title`),
    summary: t(`meta:${type}.description`),
    link: `${process.env.REACT_APP_FARM_URL}/${type}/${link}`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });
  const signal = useCancelAsyncOnUnmount()();
  const countParagraphs = (text: JSX.Element[]) => text.filter((t) => t.type === "p").length;

  const updateContent = useCallback(() => {
    if (articleOrNews) {
      // Show full article if the user is logged or if the article is not restricted to be "users only", otherwise, show article summary
      if (articleOrNews.content && (user || !articleOrNews.usersOnly)) {
        // @ts-expect-error need to convert cleanHtml() to ts
        setHtml(cleanHtml(articleOrNews.content, user, (img) => setImage(img)));
      } else if (articleOrNews.content) {
        // @ts-expect-error need to convert cleanHtml() to ts
        setHtml(cleanHtml(articleOrNews.summary, user, (img) => setImage(img)));
      }

      setShare({
        title: `${articleOrNews.htmlTitle}`,
        link: `${process.env.REACT_APP_FARM_URL}/${type}/${articleOrNews.link}`,
        summary: `${articleOrNews.htmlDescription} · ${t(`meta:${type}.description`)}`,
        image: articleOrNews.images.s,
      });
    }
  }, [user, t, type, articleOrNews]);

  const loadUserActionedArticleOrNews = useCallback(() => {
    getUserActionedArticleOrNews({
      link: link as string,
      signal,
    }).then((res: UserAction) => {
      setUserActions(res);
    });
  }, [link, signal]);

  const updateRating = (rating: RATING_STATUS) => {
    rateArticle({
      link: link as string,
      rating: rating,
      signal,
    }).then(loadUserActionedArticleOrNews);
  };

  const toggleSave = () => {
    saveArticleOrNews({
      link: link as string,
      signal,
    }).then(loadUserActionedArticleOrNews);
  };

  useEffect(() => {
    if (!articleOrNews) return;
    if (link === articleOrNews.oldLink && link !== articleOrNews.link) {
      navigate(`/${type}/${articleOrNews.link}`);
    }
    updateContent();
  }, [articleOrNews]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (link && currentLink !== link) {
      setCurrentLink(link);
      recordPageView(type, link);
      loadUserActionedArticleOrNews();
    }
  }, [currentLink, link, loadUserActionedArticleOrNews, type]);

  useEffect(() => {
    if (html.length === 0) return;
    const numberOfParagraphs = countParagraphs(html);
    setAmountOfParagraphs(numberOfParagraphs);
  }, [html]); // eslint-disable-line react-hooks/exhaustive-deps

  const [firstParagraph, firstLineBreak, secondParagraph, secondLineBreak, ...rest] = html;

  const hideManuallyAddedReadingTime = firstParagraph && null;

  const getDate = (articleOrNews: Article) => {
    if (articleOrNews.updatedAt) {
      return t("articles:post.updatedDate", { date: formatDate(articleOrNews.updatedAt, "DD.MM.YYYY", moment.ISO_8601) });
    }
    return t("articles:post.publishedDate", { date: formatDate(articleOrNews.createdAt, "DD.MM.YYYY", moment.ISO_8601) });
  };

  return (
    <SubPage
      loading={loading}
      pageName={type}
      share={share}
      crumbs={[{ name: t("navigation.knowledge"), link: user ? "/search/knowledge" : "/search?filter=articles" }, { name: t(`navigation.${type}`) }]}
    >
      {!loading && articleOrNews ? (
        <>
          <Row>
            <Col>
              <h1 data-testid="article-title">{articleOrNews.title}</h1>
            </Col>
          </Row>
          <Row className="margin-top-150">
            <Col>
              <ErrorBoundary>
                <Taglist dataTestId="article-tagList" article={articleOrNews} url={`${type.split("s")[0]}s`} />
              </ErrorBoundary>
            </Col>
          </Row>
          <Row>
            <Col>
              <Icon iconName="tabler/calendar" className="icon inline" />
              <span data-testid="article-date" className="published-date margin-right-50">
                {getDate(articleOrNews)}
              </span>
              <Icon iconName="tabler/clock" className="icon inline" />
              <span data-testid="article-reading-time" className="paragraph-m padding-left-25 margin-0">
                {t("articles:post.readingTime", { time: articleOrNews.readingTime })}
              </span>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="headline-image">
                <LazyLoadImage alt={articleOrNews.altImageText || articleOrNews.title} src={articleOrNews.images.l} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {/* @ts-expect-error need to convert to tsx */}
              <UserActions type={type} share={share} initialValue={userActions} onRatingChange={updateRating} onSavedChange={toggleSave} />
            </Col>
          </Row>
          <Row>
            <Col sm={0} md={4} />
            <Col sm={24} md={16}>
              {user && (
                <>
                  <Row>
                    <Col>
                      <div className="article-content" data-testid="article-content">
                        {[hideManuallyAddedReadingTime, firstLineBreak, secondParagraph, secondLineBreak, ...rest]}
                      </div>
                    </Col>
                  </Row>
                  <Row className="margin-top-150">
                    <Col>
                      <ErrorBoundary>
                        <Taglist dataTestId="article-tagList" article={articleOrNews} url={`${type.split("s")[0]}s`} />
                      </ErrorBoundary>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Divider color="black" />
                    </Col>
                  </Row>
                  <Row className="margin-top-100">
                    <Col>
                      {/* @ts-expect-error need to convert to tsx */}
                      <UserActions type={type} share={share} initialValue={userActions} onRatingChange={updateRating} onSavedChange={toggleSave} />
                    </Col>
                  </Row>
                </>
              )}
              {!user && articleOrNews.usersOnly && (
                <>
                  <Row>
                    <Col>
                      <div className="article-content overlay">{[hideManuallyAddedReadingTime, firstLineBreak, secondParagraph, secondLineBreak, ...rest]}</div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <SignUpNudgeCard redirectLink={`article/${articleOrNews.link}`} />
                    </Col>
                  </Row>
                </>
              )}
              {!user && !articleOrNews.usersOnly && (
                <>
                  <Row>
                    <Col>
                      <div className="article-content">{[hideManuallyAddedReadingTime, firstLineBreak, secondParagraph, secondLineBreak]}</div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <SignUpNudgeCard redirectLink={`article/${articleOrNews.link}`} />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="article-content">{rest}</div>
                    </Col>
                  </Row>
                  <Row className="margin-top-150">
                    <Col>
                      <ErrorBoundary>
                        <Taglist dataTestId="article-tagList" article={articleOrNews} url={`${type.split("s")[0]}s`} />
                      </ErrorBoundary>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Divider color="black" />
                    </Col>
                  </Row>
                  <Row className="margin-top-100">
                    <Col>
                      {/* @ts-expect-error need to convert to tsx */}
                      <UserActions type={type} share={share} initialValue={userActions} onRatingChange={updateRating} onSavedChange={toggleSave} />
                    </Col>
                  </Row>
                  {amountOfParagraphs > 6 ? (
                    <Row>
                      <Col>
                        <SignUpNudgeCard redirectLink={`article/${articleOrNews.link}`} />
                      </Col>
                    </Row>
                  ) : null}
                </>
              )}
            </Col>
            <Col sm={0} md={4} />
          </Row>
          {!loading && articleOrNews && (
            <ErrorBoundary>
              {type === ARTICLE_NEWS.ARTICLE ? <RelatedArticles link={articleOrNews.link} /> : <RelatedNews link={articleOrNews.link} />}
            </ErrorBoundary>
          )}
          {/* @ts-expect-error issue with src type */}
          <Image src={image} hidePreview={true} onVisibleChange={() => setImage(false)} />
        </>
      ) : (
        <ErrorBoundary>
          <ScreenError headline={t("errorPage.headline")} description={t("errorPage.description")} buttonText={t("errorPage.button")} />
        </ErrorBoundary>
      )}
    </SubPage>
  );
};

export default ArticlesAndNews;
